<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ArrowLeft, FileText, ImagePlus, Pencil, Plus, Trash2, XCircle } from 'lucide-vue-next'
import { formatCurrency, formatCurrencyInput, formatDate, formatDateInput, formatDecimalInput, formatIntegerInput, formatPhone, normalizeDateOnly, normalizePhone, parseCurrencyInput, parseDecimalInput, parseIntegerInput, type QuoteLineKind, type ReportVariant } from '@ck/domain'
import { CkAlert, CkButton, CkField, CkModal, CkTable, CkTabs } from '@ck/ui'
import { hasRole } from '@ck/auth'
import { ApiError, customersApi, inventoryApi, reportsApi, repairsApi, vehiclesApi, workOrdersApi, type CustomerDetail, type InventoryItem, type WorkOrderMutationResponse } from '@ck/api-client'
import { useWorkOrderCover } from '../composables/useWorkOrderCover'
import { emptyRepairDraft, emptyRepairLine, repairDraftFromRecord, validateRepairDrafts, validateWorkOrderEditLines, type LaborDraft, type PartDraft, type RepairDraft, type RepairQuoteLineDraft } from '../lib/work-order-edit'

const props = defineProps<{ id: string }>()
const router = useRouter()
const queryClient = useQueryClient()

const workOrderId = computed(() => props.id)
const workOrderQuery = useQuery({ queryKey: ['work-order', props.id], queryFn: () => workOrdersApi.get(props.id) })
const workOrder = computed(() => workOrderQuery.data.value)
const { coverUrl, uploadCover, uploading: coverUploading, uploadError: coverUploadError } = useWorkOrderCover(workOrderId)

const canManage = computed(() => hasRole('admin') || hasRole('manager'))
const isCancelled = computed(() => workOrder.value?.status === 'cancelled')

const coverInput = ref<HTMLInputElement | null>(null)
function triggerCoverUpload() { coverInput.value?.click() }
async function onCoverSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  await uploadCover(file)
}
const statusLabel = computed(() => {
  if (isCancelled.value) return 'Cancelada'
  return workOrder.value?.stages.length && workOrder.value.stages.every((s) => s.status === 'completed') ? 'Pronto' : 'Em andamento'
})
const currentStage = computed(() => workOrder.value?.stages.find((s) => s.status === 'inProgress'))
const overallProgress = computed(() => {
  const stages = workOrder.value?.stages ?? []
  return stages.length ? Math.round(stages.reduce((sum, s) => sum + s.progress, 0) / stages.length) : 0
})

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

const tab = ref<'pecas' | 'mao' | 'reparos' | 'linha'>('pecas')
const tabs = [
  { key: 'pecas', label: 'Peças' },
  { key: 'mao', label: 'Mão de obra' },
  { key: 'reparos', label: 'Reparos' },
  { key: 'linha', label: 'Linha do tempo' }
]

const repairsQuery = useQuery({ queryKey: ['repairs', props.id], queryFn: () => repairsApi.listByWorkOrder(props.id) })
const repairs = computed(() => repairsQuery.data.value ?? [])
const stageTitleById = computed(() => new Map((workOrder.value?.stages ?? []).map((s) => [s.id, s.title])))
const repairStatusLabel: Record<string, string> = { draft: 'Rascunho', pendingApproval: 'Aguardando cliente', approved: 'Aprovado', rejected: 'Recusado' }

const partColumns = [
  { key: 'code', label: 'Código', width: '110px' },
  { key: 'description', label: 'Peça', width: '1fr' },
  { key: 'qty', label: 'Qtd', width: '70px', align: 'right' as const },
  { key: 'unit', label: 'Unitário', width: '110px', align: 'right' as const },
  { key: 'total', label: 'Total', width: '110px', align: 'right' as const }
]
const partsTotal = computed(() => (workOrder.value?.parts ?? []).reduce((sum, p) => sum + p.quantity * p.unitPrice, 0))

const laborColumns = [
  { key: 'description', label: 'Serviço', width: '1fr' },
  { key: 'hours', label: 'Horas', width: '90px', align: 'right' as const },
  { key: 'rate', label: 'R$/hora', width: '110px', align: 'right' as const },
  { key: 'total', label: 'Total', width: '110px', align: 'right' as const }
]
const laborTotal = computed(() => (workOrder.value?.labor ?? []).reduce((sum, l) => sum + l.hours * l.hourlyRate, 0))

const reportVariant = ref<ReportVariant>('summary')
const reportMutation = useMutation({ mutationFn: () => reportsApi.request(props.id, reportVariant.value) })

const showEdit = ref(false)
const editError = ref<string | null>(null)
const editConflict = ref(false)
const customerSnapshot = ref<CustomerDetail | null>(null)
const editForm = ref({ customerName: '', customerPhone: '', brand: '', model: '', year: '', plate: '', vin: '', color: '', assignedTechnicianSubject: '', expectedDelivery: '' })
const editParts = ref<PartDraft[]>([])
const editLabor = ref<LaborDraft[]>([])
const editRepairs = ref<RepairDraft[]>([])
const removedRepairIds = ref<string[]>([])
const pendingRepairRemoval = ref<RepairDraft | null>(null)
const inventoryModalOpen = ref(false)
const inventorySearch = ref('')
const inventoryTarget = ref<RepairQuoteLineDraft | null>(null)

const orderedStages = computed(() => [...(workOrder.value?.stages ?? [])].sort((a, b) => a.sequence - b.sequence))
const inventoryQuery = useQuery(computed(() => ({
  queryKey: ['operational-repair-inventory', inventorySearch.value],
  queryFn: () => inventoryApi.listItems({ q: inventorySearch.value.trim(), active: true, page: 1, pageSize: 12 }),
  enabled: inventoryModalOpen.value,
  staleTime: 30_000
})))
const availableInventoryItems = computed(() => (inventoryQuery.data.value?.items ?? []).filter((item) => item.active && item.availableQuantity > 0))

async function openEdit() {
  const current = workOrder.value
  if (!current || isCancelled.value) return
  editError.value = null
  editConflict.value = false
  editForm.value = {
    customerName: current.vehicle.customer.name,
    customerPhone: formatPhone(current.vehicle.customer.phone ?? ''),
    brand: current.vehicle.brand,
    model: current.vehicle.model,
    year: String(current.vehicle.year),
    plate: current.vehicle.plate,
    vin: current.vehicle.vin ?? '',
    color: current.vehicle.color ?? '',
    assignedTechnicianSubject: current.assignedTechnicianSubject ?? '',
    expectedDelivery: current.expectedDelivery ? formatDateInput(current.expectedDelivery) : ''
  }
  editParts.value = current.parts.map((part) => ({ id: part.id, code: part.code, description: part.description, quantity: String(part.quantity), unitPrice: formatCurrency(part.unitPrice) }))
  editLabor.value = current.labor.map((entry) => ({ id: entry.id, description: entry.description, hours: String(entry.hours).replace('.', ','), hourlyRate: formatCurrency(entry.hourlyRate), technicianSubject: entry.technicianSubject }))
  editRepairs.value = []
  removedRepairIds.value = []
  pendingRepairRemoval.value = null
  try {
    const [repairResult, customer] = await Promise.all([
      repairsQuery.refetch(),
      customersApi.get(current.vehicle.customer.id)
    ])
    if (!repairResult.data) throw new Error('Não foi possível carregar os reparos da OS.')
    customerSnapshot.value = customer
    editRepairs.value = repairResult.data.map(repairDraftFromRecord)
    showEdit.value = true
  } catch (error) {
    customerSnapshot.value = null
    editError.value = error instanceof Error ? error.message : 'Não foi possível carregar os dados para correção.'
  }
}
function addPartDraft() { editParts.value.push({ id: null, code: '', description: '', quantity: '1', unitPrice: formatCurrency(0) }) }
function removePartDraft(index: number) { editParts.value.splice(index, 1) }
function addLaborDraft() { editLabor.value.push({ id: null, description: '', hours: '1', hourlyRate: formatCurrency(0), technicianSubject: null }) }
function removeLaborDraft(index: number) { editLabor.value.splice(index, 1) }
function updatePart(index: number, key: keyof PartDraft, value: string) {
  const part = editParts.value[index]
  if (!part) return
  part[key] = key === 'quantity' ? formatIntegerInput(value) : key === 'unitPrice' ? formatCurrencyInput(value) : value
}
function updateLabor(index: number, key: keyof LaborDraft, value: string) {
  const entry = editLabor.value[index]
  if (!entry) return
  entry[key] = key === 'hours' ? formatDecimalInput(value) : key === 'hourlyRate' ? formatCurrencyInput(value) : value
}

function addRepairDraft() {
  const stageId = currentStage.value?.id ?? orderedStages.value[0]?.id
  if (!stageId) {
    editError.value = 'Esta OS não possui etapas de checklist disponíveis para um novo reparo.'
    return
  }
  editRepairs.value.push(emptyRepairDraft(stageId))
  editError.value = null
}

function requestRepairRemoval(repair: RepairDraft) {
  if (!repair.id) {
    const index = editRepairs.value.indexOf(repair)
    if (index >= 0) editRepairs.value.splice(index, 1)
    return
  }
  pendingRepairRemoval.value = repair
}

function confirmRepairRemoval() {
  const repair = pendingRepairRemoval.value
  if (!repair) return
  if (repair.id && !removedRepairIds.value.includes(repair.id)) removedRepairIds.value.push(repair.id)
  const index = editRepairs.value.indexOf(repair)
  if (index >= 0) editRepairs.value.splice(index, 1)
  pendingRepairRemoval.value = null
}

function addRepairLine(repair: RepairDraft) {
  repair.lines.push(emptyRepairLine())
}

function removeRepairLine(repair: RepairDraft, index: number) {
  if (repair.lines.length <= 1) return
  repair.lines.splice(index, 1)
}

function updateRepair(index: number, key: 'title' | 'description', value: string) {
  const repair = editRepairs.value[index]
  if (repair) repair[key] = value
}

function updateRepairLine(repairIndex: number, lineIndex: number, key: 'description' | 'quantity' | 'unitPrice', value: string) {
  const repair = editRepairs.value[repairIndex]
  const line = repair?.lines[lineIndex]
  if (!line) return
  if (key === 'quantity') line.quantity = line.kind === 'part' ? formatIntegerInput(value) : formatDecimalInput(value)
  else if (key === 'unitPrice') line.unitPrice = formatCurrencyInput(value)
  else line.description = value
}

function updateRepairLineKind(line: RepairQuoteLineDraft, value: string) {
  line.kind = value as QuoteLineKind
  if (line.kind === 'labor') {
    line.inventoryItemId = null
    line.inventoryLotId = null
    line.inventorySnapshot = null
  }
  line.quantity = line.kind === 'part' ? formatIntegerInput(line.quantity) : formatDecimalInput(line.quantity)
}

function onRepairLineKindChange(line: RepairQuoteLineDraft, event: Event) {
  updateRepairLineKind(line, (event.target as HTMLSelectElement).value)
}

function openRepairInventory(line: RepairQuoteLineDraft) {
  inventoryTarget.value = line
  inventorySearch.value = ''
  inventoryModalOpen.value = true
}

function closeRepairInventory() {
  inventoryModalOpen.value = false
  inventoryTarget.value = null
}

function selectRepairInventory(item: InventoryItem) {
  const line = inventoryTarget.value
  if (!line || line.kind !== 'part') return
  line.inventoryItemId = item.id
  line.inventoryLotId = null
  line.inventorySnapshot = {
    id: item.id,
    code: item.sku,
    description: item.description ?? item.name,
    unitPrice: parseCurrencyInput(line.unitPrice) ?? 0
  }
  line.description = item.description ?? item.name
  closeRepairInventory()
}

function removeRepairInventory(line: RepairQuoteLineDraft) {
  line.inventoryItemId = null
  line.inventoryLotId = null
  line.inventorySnapshot = null
}

function repairInventoryLabel(line: RepairQuoteLineDraft) {
  return line.inventorySnapshot ? `Estoque · ${line.inventorySnapshot.code}` : 'Selecionar peça do estoque'
}

function repairLineSubtotal(line: RepairQuoteLineDraft) {
  const quantity = line.kind === 'part' ? parseIntegerInput(line.quantity) : parseDecimalInput(line.quantity)
  const unitPrice = parseCurrencyInput(line.unitPrice)
  return (quantity ?? 0) * (unitPrice ?? 0)
}

function repairTotal(repair: RepairDraft) {
  return repair.lines.reduce((sum, line) => sum + repairLineSubtotal(line), 0)
}

function repairPayload(repair: RepairDraft) {
  return {
    title: repair.title.trim(),
    description: repair.description.trim() || null,
    quoteLines: repair.lines.map((line) => ({
      kind: line.kind,
      description: line.description.trim(),
      quantity: line.kind === 'part' ? parseIntegerInput(line.quantity) ?? 0 : parseDecimalInput(line.quantity) ?? 0,
      unitPrice: parseCurrencyInput(line.unitPrice) ?? -1,
      inventoryItemId: line.inventoryItemId,
      inventoryLotId: line.inventoryLotId,
      inventorySnapshot: line.inventorySnapshot
    }))
  }
}

const editMutation = useMutation({
  mutationFn: async (): Promise<WorkOrderMutationResponse> => {
    const current = workOrder.value
    if (!current) throw new Error('OS não carregada.')
    const lineError = validateWorkOrderEditLines(editParts.value, editLabor.value)
    if (lineError) throw new Error(lineError)
    const repairError = validateRepairDrafts(editRepairs.value)
    if (repairError) throw new Error(repairError)
    if (!customerSnapshot.value) throw new Error('O cadastro do cliente não foi carregado. Feche e abra a correção novamente.')
    const dateOnly = normalizeDateOnly(editForm.value.expectedDelivery)
    if (editForm.value.expectedDelivery.trim() && !dateOnly) throw new Error('Informe uma previsão válida no formato dd/MM/yyyy.')
    const phone = normalizePhone(editForm.value.customerPhone)
    if (editForm.value.customerPhone.trim() && !phone) throw new Error('Informe um telefone válido no formato internacional.')
    const parts = editParts.value.map((part) => ({
      id: part.id,
      code: part.code.trim(),
      description: part.description.trim(),
      quantity: parseIntegerInput(part.quantity) ?? 0,
      unitPrice: parseCurrencyInput(part.unitPrice) ?? -1
    }))
    const labor = editLabor.value.map((entry) => ({
      id: entry.id,
      description: entry.description.trim(),
      hours: parseDecimalInput(entry.hours) ?? 0,
      hourlyRate: parseCurrencyInput(entry.hourlyRate) ?? -1,
      technicianSubject: entry.technicianSubject
    }))
    const customer = customerSnapshot.value
    if (customer.name !== editForm.value.customerName.trim() || (normalizePhone(customer.phone ?? '') ?? '') !== (phone ?? '')) {
      await customersApi.update(customer.id, { name: editForm.value.customerName.trim(), phone, cpfCnpj: customer.cpfCnpj, cep: customer.cep, expectedVersion: customer.version })
    }
    if (current.vehicle.version && (current.vehicle.brand !== editForm.value.brand.trim() || current.vehicle.model !== editForm.value.model.trim() || String(current.vehicle.year) !== editForm.value.year.trim() || current.vehicle.plate !== editForm.value.plate.trim().toUpperCase() || (current.vehicle.vin ?? '') !== editForm.value.vin.trim().toUpperCase() || (current.vehicle.color ?? '') !== editForm.value.color.trim())) {
      await vehiclesApi.update(current.vehicle.id, { brand: editForm.value.brand.trim(), model: editForm.value.model.trim(), year: Number(editForm.value.year), plate: editForm.value.plate.trim().toUpperCase(), vin: editForm.value.vin.trim() || null, color: editForm.value.color.trim() || null, expectedVersion: current.vehicle.version })
    }
    const response = await workOrdersApi.update(props.id, {
      expectedVersion: current.version,
      vehicleId: current.vehicleId,
      assignedTechnicianSubject: editForm.value.assignedTechnicianSubject || null,
      expectedDelivery: dateOnly ? dateOnly + 'T00:00:00.000Z' : null,
      parts,
      labor
    })
    for (const repair of editRepairs.value) {
      const payload = repairPayload(repair)
      if (repair.id) {
        if (repair.version === null) throw new Error('Reabra a correção para atualizar um reparo existente.')
        await repairsApi.update(repair.id, { ...payload, expectedVersion: repair.version })
      } else {
        await repairsApi.create(props.id, repair.stageId, payload)
      }
    }
    for (const repairId of removedRepairIds.value) await repairsApi.remove(repairId)
    return response
  },
  onSuccess: async () => { showEdit.value = false; removedRepairIds.value = []; await queryClient.invalidateQueries({ queryKey: ['work-order', props.id] }); await queryClient.invalidateQueries({ queryKey: ['work-orders'] }); await queryClient.invalidateQueries({ queryKey: ['repairs', props.id] }) },
  onError: async (error: unknown) => {
    editConflict.value = error instanceof ApiError && error.problem.status === 409
    editError.value = editConflict.value ? 'Esta OS, reparo ou cadastro relacionado foi alterado por outra pessoa. Recarregue os dados antes de tentar novamente.' : (error instanceof Error ? error.message : 'Não foi possível salvar a correção.')
    await queryClient.invalidateQueries({ queryKey: ['work-order', props.id] })
    await queryClient.invalidateQueries({ queryKey: ['repairs', props.id] })
  }
})

const showCancel = ref(false)
const cancelReason = ref('')
const cancelMutation = useMutation({
  mutationFn: () => workOrdersApi.cancel(props.id, cancelReason.value.trim() || null),
  onSuccess: async () => {
    showCancel.value = false
    cancelReason.value = ''
    await queryClient.invalidateQueries({ queryKey: ['work-order', props.id] })
    await queryClient.invalidateQueries({ queryKey: ['work-orders'] })
  }
})
</script>

<template>
  <div>
    <button type="button" class="back" @click="router.back()"><ArrowLeft :size="16"/>Voltar à fila</button>

    <CkAlert v-if="workOrderQuery.isError.value" tone="error">Não foi possível carregar esta ordem de serviço.</CkAlert>
    <p v-else-if="workOrderQuery.isPending.value" class="status-message">Carregando…</p>
    <template v-else-if="workOrder">
      <div class="header">
        <button type="button" class="photo" :class="{ 'has-image': coverUrl }" :disabled="coverUploading" :aria-label="coverUrl ? 'Trocar foto do veículo' : 'Enviar foto do veículo'" @click="triggerCoverUpload">
          <img v-if="coverUrl" :src="coverUrl" alt="Foto do veículo">
          <span v-else class="photo-hint"><ImagePlus :size="20"/>Foto</span>
          <span v-if="coverUploading" class="photo-uploading">Enviando…</span>
        </button>
        <input ref="coverInput" type="file" accept="image/*" class="sr-only" @change="onCoverSelected">
        <div class="info">
          <div class="row1"><span class="ck-code">OS {{ workOrder.number }}</span><span class="chip" :class="isCancelled ? 'cancelled' : statusLabel === 'Pronto' ? 'ok' : 'info'">{{ statusLabel }}</span></div>
          <h1>{{ workOrder.vehicle.brand }} {{ workOrder.vehicle.model }} <span class="year">{{ workOrder.vehicle.year }}</span></h1>
          <dl>
            <div><dt>Cliente</dt><dd>{{ workOrder.vehicle.customer.name }}</dd></div>
            <div><dt>Etapa atual</dt><dd>{{ currentStage?.title ?? '—' }}</dd></div>
            <div><dt>Previsão</dt><dd>{{ formatDate(workOrder.expectedDelivery) }}</dd></div>
            <div><dt>Progresso</dt><dd>{{ overallProgress }}%</dd></div>
          </dl>
        </div>
        <div class="header-actions">
          <CkButton v-if="canManage && !isCancelled" variant="secondary" @click="openEdit"><Pencil :size="16" aria-hidden="true" />Corrigir OS</CkButton>
          <div class="report-row">
            <label class="sr-only" for="report-variant">Tipo de relatório</label>
            <select id="report-variant" v-model="reportVariant" :disabled="reportMutation.isPending.value">
              <option value="summary">Resumido</option>
              <option value="full">Completo</option>
              <option value="quote">Orçamento</option>
            </select>
            <CkButton :disabled="reportMutation.isPending.value" @click="reportMutation.mutate()">
              <FileText :size="16"/>{{ reportMutation.isPending.value ? 'Solicitando…' : 'Gerar relatório' }}
            </CkButton>
          </div>
          <CkButton v-if="canManage && !isCancelled" variant="ghost" class="cancel-os" @click="showCancel = true">
            <XCircle :size="16"/>Cancelar OS
          </CkButton>
        </div>
      </div>
      <CkAlert v-if="isCancelled" tone="info" class="alert">Esta ordem de serviço foi cancelada. O checklist está bloqueado para edições.</CkAlert>
      <CkAlert v-if="coverUploadError" tone="error" class="alert">Não foi possível enviar a foto do veículo. Tente novamente.</CkAlert>
      <CkAlert v-if="reportMutation.isSuccess.value" tone="success" class="alert">Relatório solicitado — a geração acontece em segundo plano.</CkAlert>
      <CkAlert v-if="reportMutation.isError.value" tone="error" class="alert">Não foi possível solicitar o relatório.</CkAlert>

      <CkTabs :tabs="tabs" :model-value="tab" class="tabs" @update:model-value="(v) => (tab = v as typeof tab)"/>

      <CkTable v-if="tab === 'pecas'" :columns="partColumns" :rows="workOrder.parts" :row-key="(row) => row.id" empty-message="Nenhuma peça lançada.">
        <template #footer><span>Subtotal peças</span><strong class="ck-code">{{ currency.format(partsTotal) }}</strong></template>
        <template #default="{ item }">
          <span class="ck-code">{{ item.code }}</span>
          <span>{{ item.description }}</span>
          <span class="num">{{ item.quantity }}</span>
          <span class="num">{{ currency.format(item.unitPrice) }}</span>
          <span class="num strong">{{ currency.format(item.quantity * item.unitPrice) }}</span>
        </template>
      </CkTable>

      <CkTable v-if="tab === 'mao'" :columns="laborColumns" :rows="workOrder.labor" :row-key="(row) => row.id" empty-message="Nenhuma mão de obra lançada.">
        <template #footer><span>Subtotal mão de obra</span><strong class="ck-code">{{ currency.format(laborTotal) }}</strong></template>
        <template #default="{ item }">
          <span>{{ item.description }}</span>
          <span class="num">{{ item.hours }}h</span>
          <span class="num">{{ currency.format(item.hourlyRate) }}</span>
          <span class="num strong">{{ currency.format(item.hours * item.hourlyRate) }}</span>
        </template>
      </CkTable>

      <div v-if="tab === 'reparos'" class="repairs-card">
        <p v-if="repairsQuery.isPending.value" class="empty">Carregando reparos…</p>
        <p v-else-if="repairs.length === 0" class="empty">Nenhum reparo registrado. Os reparos são criados pelo mecânico no app de checklist.</p>
        <article v-for="repair in repairs" :key="repair.id" class="repair" :class="repair.status">
          <div class="repair-head">
            <div><strong>{{ repair.title }}</strong><small>{{ stageTitleById.get(repair.stageExecutionId) ?? 'Etapa' }}</small></div>
            <span class="rbadge" :class="repair.status">{{ repairStatusLabel[repair.status] ?? repair.status }}</span>
          </div>
          <p v-if="repair.description" class="repair-desc">{{ repair.description }}</p>
          <ul class="repair-lines">
            <li v-for="line in repair.quoteLines" :key="line.id">
              <span>{{ line.kind === 'labor' ? 'Mão de obra' : 'Peça' }} · {{ line.description }}<small v-if="line.inventorySnapshot"> · Estoque {{ line.inventorySnapshot.code }}</small></span>
              <span class="num">{{ line.quantity }} × {{ currency.format(line.unitPrice) }}</span>
            </li>
          </ul>
          <div class="repair-foot">
            <span v-if="repair.status === 'rejected' && repair.customerDecisionNote" class="reason">Recusado: {{ repair.customerDecisionNote }}</span><span v-else></span>
            <strong class="num">{{ currency.format(repair.total) }}</strong>
          </div>
        </article>
      </div>

      <div v-if="tab === 'linha'" class="timeline-card">
        <p v-if="workOrder.timeline.length === 0" class="empty">Nenhum evento registrado ainda.</p>
        <article v-for="event in [...workOrder.timeline].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))" :key="event.id" class="event">
          <span class="dot" aria-hidden="true"/>
          <div><small class="ck-code">{{ formatDate(event.occurredAt) }}</small><p>{{ event.description }}</p><span class="actor">por {{ event.actorSubject ?? 'sistema' }}</span></div>
        </article>
      </div>
    </template>

    <CkModal v-if="showCancel" title="Cancelar ordem de serviço" @close="showCancel = false">
      <p class="cancel-copy">Esta ação marca a OS como cancelada, remove-a da fila ativa e bloqueia novas edições do checklist. Não pode ser desfeita.</p>
      <label class="cancel-field">
        <span>Motivo (opcional)</span>
        <textarea v-model="cancelReason" rows="3" placeholder="Ex.: cliente desistiu do serviço" :disabled="cancelMutation.isPending.value"></textarea>
      </label>
      <CkAlert v-if="cancelMutation.isError.value" tone="error">Não foi possível cancelar. Tente novamente.</CkAlert>
      <div class="cancel-actions">
        <CkButton variant="ghost" :disabled="cancelMutation.isPending.value" @click="showCancel = false">Voltar</CkButton>
        <CkButton variant="ghost" class="confirm-cancel" :disabled="cancelMutation.isPending.value" @click="cancelMutation.mutate()">
          {{ cancelMutation.isPending.value ? 'Cancelando…' : 'Confirmar cancelamento' }}
        </CkButton>
      </div>
    </CkModal>

    <CkModal v-if="showEdit" title="Corrigir ordem de serviço" @close="showEdit = false">
      <form class="edit-form" @submit.prevent="editMutation.mutate()">
        <CkAlert v-if="editConflict" tone="error">Conflito de versão: recarregue a OS antes de salvar outra correção.</CkAlert>
        <CkAlert v-else-if="editError" tone="error">{{ editError }}</CkAlert>
        <p class="edit-note">Número, status e histórico não podem ser alterados. Linhas removidas ficam baixadas para auditoria.</p>
        <h3>Cliente e veículo</h3>
        <div class="edit-grid">
          <CkField v-model="editForm.customerName" label="Cliente" required :maxlength="120" />
          <CkField :model-value="editForm.customerPhone" label="Telefone" placeholder="+00 (00) 00000-0000" inputmode="tel" :maxlength="19" @update:model-value="editForm.customerPhone = formatPhone($event)" />
          <CkField v-model="editForm.brand" label="Marca" required :maxlength="60" />
          <CkField v-model="editForm.model" label="Modelo" required :maxlength="80" />
          <CkField v-model="editForm.year" label="Ano" inputmode="numeric" :maxlength="4" />
          <CkField v-model="editForm.plate" label="Placa" required :maxlength="10" />
          <CkField v-model="editForm.vin" label="VIN / chassi" :maxlength="40" />
          <CkField v-model="editForm.color" label="Cor" :maxlength="40" />
        </div>
        <div class="edit-grid">
          <CkField v-model="editForm.assignedTechnicianSubject" label="Técnico (subject)" hint="Deixe vazio para remover a atribuição" :maxlength="120" />
          <CkField :model-value="editForm.expectedDelivery" label="Previsão de entrega" placeholder="dd/MM/yyyy" inputmode="numeric" :maxlength="10" @update:model-value="editForm.expectedDelivery = formatDateInput($event)" />
        </div>
        <div class="line-heading"><h3>Peças</h3><CkButton type="button" variant="ghost" @click="addPartDraft"><Plus :size="15" aria-hidden="true" />Adicionar</CkButton></div>
        <div v-for="(part, index) in editParts" :key="part.id || index" class="line-grid">
          <CkField :model-value="part.code" label="Código" :maxlength="40" @update:model-value="updatePart(index, 'code', $event)" />
          <CkField :model-value="part.description" label="Descrição" :maxlength="120" @update:model-value="updatePart(index, 'description', $event)" />
          <CkField :model-value="part.quantity" label="Qtd. inteira" inputmode="numeric" :maxlength="8" @update:model-value="updatePart(index, 'quantity', $event)" />
          <CkField :model-value="part.unitPrice" label="Valor unitário" inputmode="decimal" :maxlength="14" @update:model-value="updatePart(index, 'unitPrice', $event)" />
          <button type="button" class="remove-line" aria-label="Remover peça" @click="removePartDraft(index)"><Trash2 :size="16" aria-hidden="true" /></button>
        </div>
        <div class="line-heading"><h3>Mão de obra</h3><CkButton type="button" variant="ghost" @click="addLaborDraft"><Plus :size="15" aria-hidden="true" />Adicionar</CkButton></div>
        <div v-for="(entry, index) in editLabor" :key="entry.id || index" class="line-grid labor-grid">
          <CkField :model-value="entry.description" label="Descrição" :maxlength="120" @update:model-value="updateLabor(index, 'description', $event)" />
          <CkField :model-value="entry.hours" label="Horas" inputmode="decimal" :maxlength="8" @update:model-value="updateLabor(index, 'hours', $event)" />
          <CkField :model-value="entry.hourlyRate" label="Valor/hora" inputmode="decimal" :maxlength="14" @update:model-value="updateLabor(index, 'hourlyRate', $event)" />
          <button type="button" class="remove-line" aria-label="Remover mão de obra" @click="removeLaborDraft(index)"><Trash2 :size="16" aria-hidden="true" /></button>
        </div>
        <section class="repair-edit-section" aria-labelledby="repair-edit-title">
          <div class="line-heading"><h3 id="repair-edit-title">Reparos e orçamentos do checklist</h3><CkButton type="button" variant="ghost" @click="addRepairDraft"><Plus :size="15" aria-hidden="true" />Adicionar reparo</CkButton></div>
          <p class="edit-note">A visão operacional mostra todos os reparos. Alterar um reparo já enviado ou decidido volta o orçamento para rascunho e exige novo envio ao cliente.</p>
          <p v-if="editRepairs.length === 0" class="empty-edit">Nenhum reparo registrado. Use “Adicionar reparo” para corrigir ou lançar um orçamento.</p>
          <article v-for="(repair, repairIndex) in editRepairs" :key="repair.id || `new-repair-${repairIndex}`" class="repair-edit-card">
            <div class="repair-edit-head">
              <div><strong>{{ repair.id ? 'Reparo registrado' : 'Novo reparo' }}</strong><span class="rbadge" :class="repair.status">{{ repairStatusLabel[repair.status] }}</span></div>
              <button type="button" class="remove-line repair-remove" :aria-label="`Remover ${repair.title || 'reparo'}`" @click="requestRepairRemoval(repair)"><Trash2 :size="16" aria-hidden="true" /></button>
            </div>
            <div class="edit-grid repair-meta-grid">
              <CkField :model-value="repair.title" label="Título do reparo" placeholder="Ex.: Troca do coxim" :maxlength="120" @update:model-value="updateRepair(repairIndex, 'title', $event)" />
              <label class="select-field"><span>Etapa do checklist</span><select v-model="repair.stageId" :disabled="!!repair.id" aria-label="Etapa do checklist do reparo">
                <option value="" disabled>Selecione uma etapa</option>
                <option v-for="stage in orderedStages" :key="stage.id" :value="stage.id">{{ stage.sequence }} · {{ stage.title }}</option>
              </select></label>
              <CkField :model-value="repair.description" class="repair-description-field" label="Descrição (opcional)" :maxlength="500" @update:model-value="updateRepair(repairIndex, 'description', $event)" />
            </div>
            <p v-if="repair.status !== 'draft'" class="repair-edit-warning">Ao salvar alterações neste reparo, o status será Rascunho e a aprovação anterior será limpa.</p>
            <div class="line-heading nested-heading"><h4>Linhas do orçamento</h4><CkButton type="button" variant="ghost" @click="addRepairLine(repair)"><Plus :size="14" aria-hidden="true" />Adicionar linha</CkButton></div>
            <div v-for="(line, lineIndex) in repair.lines" :key="line.id || `${repairIndex}-${lineIndex}`" class="quote-line-grid">
              <label class="select-field line-kind-field"><span>Tipo</span><select :value="line.kind" aria-label="Tipo da linha do orçamento" @change="onRepairLineKindChange(line, $event)">
                <option value="part">Peça</option>
                <option value="labor">Mão de obra</option>
              </select></label>
              <CkField :model-value="line.description" label="Descrição" :maxlength="160" @update:model-value="updateRepairLine(repairIndex, lineIndex, 'description', $event)" />
              <CkField :model-value="line.quantity" :label="line.kind === 'part' ? 'Quantidade inteira' : 'Horas'" :inputmode="line.kind === 'part' ? 'numeric' : 'decimal'" :maxlength="line.kind === 'part' ? 6 : 9" :placeholder="line.kind === 'part' ? '1' : '0,00'" @update:model-value="updateRepairLine(repairIndex, lineIndex, 'quantity', $event)" />
              <CkField :model-value="line.unitPrice" label="Valor unitário" inputmode="decimal" :maxlength="14" placeholder="R$ 0,00" @update:model-value="updateRepairLine(repairIndex, lineIndex, 'unitPrice', $event)" />
              <button type="button" class="remove-line" aria-label="Remover linha do orçamento" :disabled="repair.lines.length <= 1" @click="removeRepairLine(repair, lineIndex)"><Trash2 :size="16" aria-hidden="true" /></button>
              <div v-if="line.kind === 'part'" class="repair-inventory-tools">
                <CkButton type="button" variant="secondary" class="inventory-button" @click="openRepairInventory(line)">{{ repairInventoryLabel(line) }}</CkButton>
                <CkButton v-if="line.inventoryItemId" type="button" variant="ghost" class="inventory-remove" @click="removeRepairInventory(line)">Remover vínculo</CkButton>
                <span v-if="line.inventorySnapshot" class="inventory-snapshot">Snapshot: {{ line.inventorySnapshot.description }} · {{ currency.format(line.inventorySnapshot.unitPrice) }}</span>
              </div>
            </div>
            <div class="repair-edit-total"><span>Subtotal do reparo</span><strong class="num">{{ currency.format(repairTotal(repair)) }}</strong></div>
          </article>
        </section>
        <div class="edit-actions"><CkButton type="button" variant="ghost" :disabled="editMutation.isPending.value" @click="showEdit = false">Cancelar</CkButton><CkButton type="submit" :disabled="editMutation.isPending.value">{{ editMutation.isPending.value ? 'Salvando…' : 'Salvar correção' }}</CkButton></div>
      </form>
    </CkModal>

    <CkModal v-if="pendingRepairRemoval" title="Arquivar reparo" @close="pendingRepairRemoval = null">
      <p class="cancel-copy">O reparo será retirado das visões ativas, mas suas linhas de orçamento, decisão do cliente, vínculos de estoque e auditoria serão preservados.</p>
      <p class="original-repair"><strong>{{ pendingRepairRemoval.title || 'Reparo sem título' }}</strong><span>{{ repairStatusLabel[pendingRepairRemoval.status] }}</span></p>
      <div class="cancel-actions">
        <CkButton type="button" variant="ghost" @click="pendingRepairRemoval = null">Voltar</CkButton>
        <CkButton type="button" variant="secondary" class="confirm-cancel" @click="confirmRepairRemoval">Confirmar arquivamento</CkButton>
      </div>
    </CkModal>

    <CkModal v-if="inventoryModalOpen" title="Selecionar peça do estoque" @close="closeRepairInventory">
      <CkField v-model="inventorySearch" label="Buscar peça" placeholder="SKU ou descrição" inputmode="search" :maxlength="80" />
      <CkAlert v-if="inventoryQuery.isError.value" tone="error">Não foi possível carregar as peças disponíveis.</CkAlert>
      <p v-else-if="inventoryQuery.isPending.value" class="empty-edit">Carregando peças disponíveis…</p>
      <p v-else-if="availableInventoryItems.length === 0" class="empty-edit">Nenhuma peça disponível para esta busca.</p>
      <ul v-else class="inventory-options">
        <li v-for="item in availableInventoryItems" :key="item.id">
          <CkButton type="button" variant="ghost" class="inventory-option" @click="selectRepairInventory(item)">
            <span><strong>{{ item.description ?? item.name }}</strong><small>{{ item.sku }} · {{ item.availableQuantity }} disponível(is)</small></span>
          </CkButton>
        </li>
      </ul>
    </CkModal>
  </div>
</template>

<style scoped>
.back { display: inline-flex; align-items: center; gap: 7px; margin-bottom: 18px; padding: 8px 13px; border: 1px solid var(--color-border); border-radius: 9px; background: transparent; color: var(--color-text-muted); font-size: 13px; cursor: pointer; }
.status-message { padding: 20px 0; color: var(--color-text-muted); font-size: 13px; }
.header { display: flex; gap: 18px; align-items: flex-start; margin-bottom: 22px; }
.photo { position: relative; display: flex; align-items: center; justify-content: center; width: 180px; height: 120px; flex-shrink: 0; padding: 0; border: 0; border-radius: var(--radius-lg); overflow: hidden; cursor: pointer; background: linear-gradient(135deg, var(--ck-maroon-700), var(--ck-ink-900)); color: var(--ck-cream-100); }
.photo:disabled { cursor: progress; }
.photo img { width: 100%; height: 100%; object-fit: cover; }
.photo-hint { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; opacity: .85; }
.photo-uploading { position: absolute; inset: auto 0 0 0; padding: 4px; font-size: 11px; text-align: center; background: rgba(0, 0, 0, .5); color: #fff; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); }
.info { flex: 1; }
.row1 { display: flex; align-items: center; gap: 12px; }
.row1 .ck-code { color: var(--ck-amber-400); font-weight: 700; font-size: 12px; }
.chip { padding: 3px 10px; border-radius: 99px; font-size: 11.5px; font-weight: 700; }
.chip.ok { background: var(--ck-ok-bg); color: var(--ck-ok-text); }
.chip.info { background: var(--ck-info-bg); color: var(--ck-info-text); }
.chip.cancelled { background: var(--ck-fail-bg); color: var(--ck-fail-text); }
.header-actions { display: flex; flex-direction: column; gap: 8px; flex-shrink: 0; }
.report-row { display: flex; gap: 8px; align-items: stretch; }
.report-row select { min-height: 40px; padding: 0 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-surface); color: var(--color-text); font: inherit; }
.repairs-card { display: grid; gap: 12px; }
.repair { padding: 14px 16px; border: 1px solid var(--color-border); border-left: 4px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.repair.approved { border-left-color: var(--ck-ok-500); }
.repair.rejected { border-left-color: var(--ck-fail-500); }
.repair.pendingApproval { border-left-color: var(--ck-orange-500); }
.repair-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.repair-head small { display: block; margin-top: 2px; color: var(--color-text-faint); font-size: 11.5px; }
.rbadge { font-size: 11.5px; font-weight: 700; padding: 3px 10px; border-radius: 99px; background: var(--color-surface-muted); color: var(--color-text-muted); }
.rbadge.approved { background: var(--ck-ok-bg); color: var(--ck-ok-text); }
.rbadge.rejected { background: var(--ck-fail-bg); color: var(--ck-fail-text); }
.repair-desc { margin: 8px 0 0; font-size: 13px; color: var(--color-text-muted); }
.repair-lines { list-style: none; margin: 10px 0; padding: 0; display: grid; gap: 5px; }
.repair-lines li { display: flex; justify-content: space-between; gap: 10px; font-size: 13px; }
.repair-foot { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-top: 8px; }
.repair-foot .reason { color: var(--ck-fail-text); font-size: 12.5px; }
.cancel-os { color: var(--ck-fail-text); }
.cancel-copy { margin: 0 0 16px; color: var(--color-text-muted); font-size: 14px; }
.cancel-field { display: block; margin-bottom: 16px; }
.cancel-field span { display: block; margin-bottom: 6px; font-size: 13px; font-weight: 600; color: var(--color-text-muted); }
.cancel-field textarea { width: 100%; padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); color: var(--color-text); font: inherit; resize: vertical; }
.cancel-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 8px; }
.edit-form { display: grid; gap: var(--space-3); min-width: 0; }
.edit-form h3 { margin: var(--space-2) 0 0; font-size: var(--fs-md); }
.edit-note { margin: 0; color: var(--color-text-muted); font-size: var(--fs-sm); line-height: var(--lh-normal); }
.edit-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-4); min-width: 0; }
.select-field { display: block; min-width: 0; margin: 0; }
.select-field span { display: block; margin-bottom: var(--space-1); color: var(--color-text-muted); font-size: var(--fs-sm); font-weight: var(--fw-semibold); }
.select-field select { width: 100%; min-height: 44px; padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); color: var(--color-text); font: inherit; }
.line-heading { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); margin-top: var(--space-2); }
.line-heading h3 { margin: 0; }
.line-grid { display: grid; grid-template-columns: minmax(100px, .8fr) minmax(160px, 1.5fr) minmax(80px, .6fr) minmax(110px, .8fr) 36px; gap: var(--space-3); align-items: start; min-width: 0; padding: var(--space-3); border: 1px solid var(--color-border); border-radius: var(--radius-md); }
.line-grid :deep(.ck-field) { min-width: 0; margin-bottom: 0; }
.labor-grid { grid-template-columns: minmax(160px, 1.5fr) minmax(80px, .7fr) minmax(110px, .8fr) 36px; }
.remove-line { display: inline-flex; align-items: center; align-self: start; justify-content: center; flex-shrink: 0; width: 32px; height: 32px; margin-top: 26px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: transparent; color: var(--ck-fail-text); cursor: pointer; }
.remove-line:hover, .remove-line:focus-visible { border-color: var(--ck-fail-500); }
.repair-edit-section { display: grid; gap: var(--space-3); margin-top: var(--space-2); padding-top: var(--space-4); border-top: 1px solid var(--color-border); }
.repair-edit-section > .edit-note { margin-top: calc(var(--space-2) * -1); }
.repair-edit-card { display: grid; gap: var(--space-3); min-width: 0; padding: var(--space-4); border: 1px solid var(--color-border); border-left: 3px solid var(--ck-orange-500); border-radius: var(--radius-lg); background: var(--color-surface-muted); }
.repair-edit-head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
.repair-edit-head > div { display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); min-width: 0; }
.repair-edit-head strong { color: var(--color-text); }
.rbadge { display: inline-flex; width: fit-content; padding: 3px 9px; border-radius: var(--radius-full); background: var(--color-surface); color: var(--color-text-muted); font-size: var(--fs-xs); font-weight: var(--fw-bold); }
.rbadge.pendingApproval { background: var(--ck-warn-bg); color: var(--ck-warn-text); }
.rbadge.approved { background: var(--ck-ok-bg); color: var(--ck-ok-text); }
.rbadge.rejected { background: var(--ck-fail-bg); color: var(--ck-fail-text); }
.repair-description-field { grid-column: 1 / -1; }
.repair-edit-warning { margin: 0; padding: var(--space-2) var(--space-3); border-radius: var(--radius-md); background: var(--ck-warn-bg); color: var(--ck-warn-text); font-size: var(--fs-xs); line-height: var(--lh-normal); }
.nested-heading { margin-top: 0; }
.nested-heading h4 { margin: 0; color: var(--color-text); font-size: var(--fs-sm); }
.quote-line-grid { display: grid; grid-template-columns: minmax(96px, .75fr) minmax(150px, 1.5fr) minmax(86px, .7fr) minmax(112px, .85fr) 36px; gap: var(--space-3); align-items: start; min-width: 0; padding: var(--space-3); border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-surface); }
.quote-line-grid :deep(.ck-field) { min-width: 0; margin-bottom: 0; }
.line-kind-field { margin: 0; }
.quote-line-grid .remove-line { margin-top: 26px; }
.repair-inventory-tools { grid-column: 2 / -1; display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); min-width: 0; }
.inventory-button, .inventory-remove { min-height: 36px; padding: 7px 10px; font-size: var(--fs-xs); }
.inventory-button { display: inline-flex; align-items: center; }
.inventory-remove { color: var(--ck-maroon-700); }
.inventory-snapshot { min-width: 0; overflow-wrap: anywhere; color: var(--color-text-muted); font-size: var(--fs-xs); }
.inventory-options { display: grid; gap: var(--space-2); max-height: 360px; margin: var(--space-3) 0 0; padding: 0; overflow-y: auto; list-style: none; }
.inventory-option { display: flex; align-items: center; justify-content: flex-start; width: 100%; min-height: 56px; padding: var(--space-2) var(--space-3); text-align: left; }
.inventory-option span { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.inventory-option small { color: var(--color-text-muted); font-size: var(--fs-2xs); }
.repair-edit-total { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); color: var(--color-text-muted); font-size: var(--fs-sm); }
.repair-edit-total strong { color: var(--color-text); }
.empty-edit { margin: 0; padding: var(--space-3); border: 1px dashed var(--color-border); border-radius: var(--radius-md); color: var(--color-text-muted); font-size: var(--fs-sm); }
.original-repair { display: flex; flex-direction: column; gap: 3px; margin: 0 0 var(--space-4); padding: var(--space-3); border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-surface-muted); }
.original-repair span { color: var(--color-text-muted); font-size: var(--fs-xs); }
.edit-actions { display: flex; justify-content: flex-end; gap: var(--space-2); margin-top: var(--space-2); }
.confirm-cancel { background: var(--ck-fail-500); color: #fff; }
.confirm-cancel:hover { filter: brightness(.95); }
h1 { margin: 6px 0 12px; font-family: var(--font-display); font-size: 28px; font-weight: 800; }
.year { color: var(--color-text-faint); font-weight: 600; }
dl { display: flex; gap: 26px; flex-wrap: wrap; }
dt { font-size: 10.5px; font-weight: 700; letter-spacing: var(--ls-wide); text-transform: uppercase; color: var(--color-text-faint); }
dd { margin: 3px 0 0; font-size: 14px; }
.alert { margin-bottom: 16px; }
.tabs { max-width: 420px; margin-bottom: 18px; }
.num { text-align: right; font-family: var(--font-mono); font-size: 13px; }
.strong { font-weight: 700; color: var(--color-text); }
.timeline-card { padding: 20px 24px; border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.event { display: flex; gap: 14px; padding-bottom: 18px; }
.event:last-child { padding-bottom: 0; }
.event .dot { flex-shrink: 0; width: 11px; height: 11px; margin-top: 3px; border-radius: 99px; background: var(--ck-flame); }
.event small { color: var(--ck-amber-400); font-size: 11.5px; }
.event p { margin: 2px 0; font-size: 14px; }
.actor { color: var(--color-text-faint); font-size: 12px; }
.empty { color: var(--color-text-muted); font-size: 13px; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); }
@media (max-width: 620px) {
  .edit-grid { grid-template-columns: 1fr; }
  .line-grid, .labor-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-3); }
  .line-grid :deep(.ck-field):nth-child(1), .line-grid :deep(.ck-field):nth-child(2) { grid-column: 1 / -1; }
  .remove-line { grid-column: 2; justify-self: end; margin-top: 0; }
  .quote-line-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-3); }
  .quote-line-grid :deep(.ck-field):nth-child(2) { grid-column: 1 / -1; }
  .quote-line-grid .remove-line { grid-column: 2; margin-top: 0; }
  .repair-inventory-tools, .inventory-snapshot { grid-column: 1 / -1; }
  .edit-actions { flex-wrap: wrap; }
  .edit-actions :deep(.ck-button) { flex: 1; }
}
</style>
